import { useEffect, useMemo, useState, useRef } from 'react';
import {
  getCategories,
  getResources,
  getResourceTypes,
  deleteResource,
  deleteCategory,
  deleteResourceType,
  type Category,
  type Resource,
  type ResourceTypeEntity,
} from '../api/resourceService';
import ResourceCard from '../components/ResourceCard';
import ResourceForm from '../components/ResourceForm';
import CategoriesModal from '../components/CategoriesModal';
import ResourceTypesModal from '../components/ResourceTypesModal';
import LoadingOverlay from '../components/LoadingOverlay';
import { useAuth } from '../contexts/AuthContext';
import SearchBar from '../components/SearchBar';
import WelcomePages from '../components/WelcomePages';
import { useTranslation } from 'react-i18next';
import CategoryForm from '../components/CategoryForm';
import ResourceTypeForm from '../components/ResourceTypeForm';
import ConfirmDialog from '../components/ConfirmDialog';

export default function ResourcesPage() {
  const { isAdmin } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeEntity[]>([]);
  const [searchResults, setSearchResults] = useState<Resource[]>([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreResources, setHasMoreResources] = useState(true);
  
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastResourceRef = useRef<HTMLDivElement | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Resource | undefined>(undefined);

  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>(undefined);
  
  const [isResourceTypesModalOpen, setIsResourceTypesModalOpen] = useState(false);
  const [isResourceTypeFormOpen, setIsResourceTypeFormOpen] = useState(false);
  const [editingResourceType, setEditingResourceType] = useState<ResourceTypeEntity | undefined>(undefined);
  
  // Estado para el modo de vista
  const [viewMode, setViewMode] = useState<'standard' | 'compact'>(() => {
    const saved = localStorage.getItem('devhub-view-mode');
    return (saved as 'standard' | 'compact') || 'compact';
  });
  
  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    resourceType: ResourceTypeEntity | null;
  }>({
    isOpen: false,
    resourceType: null,
  });

  const [confirmDeleteCategory, setConfirmDeleteCategory] = useState<{
    isOpen: boolean;
    category: Category | null;
  }>({
    isOpen: false,
    category: null,
  });

  async function loadAll() {
    setIsLoading(true);
    setError(null);
    try {
      const [cats, resResponse, types] = await Promise.all([
        getCategories(), 
        getResources(undefined, undefined, 1, itemsPerPage), 
        getResourceTypes()
      ]);
      setCategories(cats);
      setResources(resResponse.resources);
      setResourceTypes(types);
      
      setCurrentPage(resResponse.pagination.page);
      setTotalPages(resResponse.pagination.totalPages);
      setHasMoreResources(resResponse.pagination.hasNextPage);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (!lastResourceRef.current || isSearchActive) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [lastEntry] = entries;
        if (lastEntry.isIntersecting && hasMoreResources && !isLoadingMore) {
          loadMoreResources();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;
    observer.observe(lastResourceRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isSearchActive, hasMoreResources, isLoadingMore, currentPage]);

  const filtered = useMemo(() => {
    return isSearchActive ? searchResults : resources;
  }, [resources, searchResults, isSearchActive]);

  const handleSearchResults = (results: Resource[], _total: number, hasFilters: boolean) => {
    setSearchResults(results);
    setIsSearchActive(hasFilters);
    
    if (hasFilters) {
      setCurrentPage(1);
      setHasMoreResources(false);
    } else {
      setHasMoreResources(true);
    }
  };

  const loadPage = async (page: number) => {
    if (page < 1 || page > totalPages) return;
    
    setIsLoading(true);
    try {
      const response = await getResources(undefined, undefined, page, itemsPerPage);
      setResources(response.resources);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    loadPage(newPage);
  };

  const loadMoreResources = async () => {
    if (isLoadingMore || !hasMoreResources || isSearchActive) return;
    
    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await getResources(undefined, undefined, nextPage, itemsPerPage);
      setResources(prev => [...prev, ...response.resources]);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setHasMoreResources(response.pagination.hasNextPage);
    } catch (e) {
      console.error('Error loading more resources:', e);
      setError((e as Error).message);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const openCreate = () => {
    if (!isAdmin) return;
    setEditing(undefined);
    setIsModalOpen(true);
  };
  const openEdit = (r: Resource) => {
    setEditing(r);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  const onSaved = async () => {
    closeModal();
    await loadAll();
  };

  const onDelete = async (r: Resource) => {
    try {
      if (!confirm(t('common.delete') + ' ' + t('common.resource') + '?')) return;
      await deleteResource(r.id);
      await loadAll();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const openCategoriesModal = () => {
    if (!isAdmin) return;
    setIsCategoriesModalOpen(true);
  };

  const openResourceTypesModal = () => {
    if (!isAdmin) return;
    setIsResourceTypesModalOpen(true);
  };

  const handleDeleteResourceType = (resourceType: ResourceTypeEntity) => {
    setConfirmDelete({
      isOpen: true,
      resourceType,
    });
  };

  const confirmDeleteResourceType = async () => {
    if (!confirmDelete.resourceType) return;
    
    try {
      await deleteResourceType(confirmDelete.resourceType.id);
      await loadAll();
      setConfirmDelete({ isOpen: false, resourceType: null });
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const cancelDeleteResourceType = () => {
    setConfirmDelete({ isOpen: false, resourceType: null });
  };

  const handleDeleteCategory = (category: Category) => {
    setConfirmDeleteCategory({
      isOpen: true,
      category,
    });
  };

  const confirmDeleteCategoryAction = async () => {
    if (!confirmDeleteCategory.category) return;
    
    try {
      await deleteCategory(confirmDeleteCategory.category.id);
      await loadAll();
      setConfirmDeleteCategory({ isOpen: false, category: null });
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const cancelDeleteCategory = () => {
    setConfirmDeleteCategory({ isOpen: false, category: null });
  };

  const handleViewModeChange = (newViewMode: 'standard' | 'compact') => {
    setViewMode(newViewMode);
    localStorage.setItem('devhub-view-mode', newViewMode);
  };

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && <LoadingOverlay message={t('resourcePage.loading')} />}
      {error && (
        <div
          className="card"
          style={{
            padding: 12,
            borderColor: 'var(--danger)',
            color: 'var(--danger)',
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}
      <WelcomePages 
        showButton={true} 
        onAddResource={openCreate}
        onOpenCategories={openCategoriesModal}
        onOpenResourceTypes={openResourceTypesModal}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      <SearchBar
        totalResources={resources.length}
        categoryList={categories}
        onSearchResults={handleSearchResults}
      />
      
      <div 
        className={`grid gap-3 overflow-y-auto max-h-[400px] sm:max-h-[500px] md:max-h-[600px] lg:max-h-[500px] xl:max-h-[600px] pt-2 ${
          viewMode === 'standard'
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'
        }`}
      >
        {filtered.map((r, index) => (
          <div 
            key={r.id} 
            style={{ display: 'grid', gap: 3 }}
            ref={index === filtered.length - 1 && !isSearchActive ? lastResourceRef : null}
          >
            <ResourceCard 
              resource={r} 
              categories={categories} 
              resourceTypes={resourceTypes}
              viewMode={viewMode}
              onDelete={() => onDelete(r)}
              onEdit={() => openEdit(r)} 
            />
          </div>
        ))}
        {!filtered.length && (
          <div
            className="card"
            style={{ padding: 24, color: 'var(--text-muted)' }}
          >
            {isSearchActive ? t('resourcePage.noResultsFilters') : t('resourcePage.noResults')}
          </div>
        )}
      </div>

      {isLoadingMore && !isSearchActive && (
        <div className="flex justify-center items-center mt-8">
          <div className="flex items-center gap-2 text-[var(--text-muted)]">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--text-default)]"></div>
            <span>{t('lazyLoading.loadingMore')}</span>
          </div>
        </div>
      )}

      {!isSearchActive && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--border)] transition-colors"
          >
            {t('pagination.previous')}
          </button>
          
          <span className="px-4 py-2 text-[var(--text-muted)]">
            {t('pagination.page', { current: currentPage, total: totalPages })}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--border)] transition-colors"
          >
            {t('pagination.next')}
          </button>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl max-w-4xl w-full min-h-[88vh] max-h-[88vh] overflow-y-auto">
            <ResourceForm
              initial={editing}
              onSaved={onSaved}
              onCancel={closeModal}
            />
          </div>
        </div>
      )}

      {isCategoriesModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl max-w-4xl w-full min-h-[88vh] max-h-[88vh] overflow-y-auto">
            <CategoriesModal
              categories={categories}
              onCancel={() => setIsCategoriesModalOpen(false)}
              onDelete={handleDeleteCategory}
              onOpenCreate={() => {
                setEditingCategory(undefined);
                setIsCatModalOpen(true);
              }}
              onOpenEdit={(category: Category) => {
                setEditingCategory(category);
                setIsCatModalOpen(true);
              }}
            />
          </div>
        </div>
      )}

      {isCatModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl max-w-3xl w-full max-h-[50vh] overflow-y-auto">
            <CategoryForm
              initial={editingCategory}
              onSaved={async () => {
                setIsCatModalOpen(false);
                setEditingCategory(undefined);
                await loadAll();
              }}
              onCancel={() => {
                setIsCatModalOpen(false);
                setEditingCategory(undefined);
              }}
            />
          </div>
        </div>
      )}

      {isResourceTypesModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" style={{ zIndex: 9999 }}>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl max-w-4xl w-full min-h-[88vh] max-h-[88vh] overflow-y-auto">
            <ResourceTypesModal
              resourceTypes={resourceTypes}
              onCancel={() => {
                setIsResourceTypesModalOpen(false);
              }}
              onDelete={handleDeleteResourceType}
              onOpenCreate={() => {
                setEditingResourceType(undefined);
                setIsResourceTypeFormOpen(true);
              }}
              onOpenEdit={(resourceType: ResourceTypeEntity) => {
                setEditingResourceType(resourceType);
                setIsResourceTypeFormOpen(true);
              }}
            />
          </div>
        </div>
      )}

      {isResourceTypeFormOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" style={{ zIndex: 10000 }}>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl max-w-3xl w-full max-h-[50vh] overflow-y-auto">
            <ResourceTypeForm
              initial={editingResourceType}
              onSaved={async () => {
                setIsResourceTypeFormOpen(false);
                setEditingResourceType(undefined);
                await loadAll();
              }}
              onCancel={() => {
                setIsResourceTypeFormOpen(false);
                setEditingResourceType(undefined);
              }}
            />
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete.isOpen}
        title={t('resourcePage.resourceTypesModalDelete.title')}
        message={t('resourcePage.resourceTypesModalDelete.subtitle', { resourceTypeName: confirmDelete.resourceType?.name[lang] })}
        confirmText={t('resourcePage.resourceTypesModalDelete.delete')}
        cancelText={t('resourcePage.resourceTypesModalDelete.cancel')}
        confirmButtonStyle="danger"
        onConfirm={confirmDeleteResourceType}
        onCancel={cancelDeleteResourceType}
      />
      <ConfirmDialog
        isOpen={confirmDeleteCategory.isOpen}
        title={t('resourcePage.categoriesModalDelete.title')}
        message={t('resourcePage.categoriesModalDelete.subtitle', { categoryName: confirmDeleteCategory.category?.name[lang] })}
        confirmText={t('resourcePage.categoriesModalDelete.delete')}
        cancelText={t('resourcePage.categoriesModalDelete.cancel')}
        confirmButtonStyle="danger"
        onConfirm={confirmDeleteCategoryAction}
        onCancel={cancelDeleteCategory}
      />
    </div>
  );
}
